/**
 * TagIconPicker — the corporate dashboard's patient-tag icon picker:
 *   - the field shows the chosen icon and its path, or "Choose an icon";
 *   - its "Choose Icon" dialog lists the shared icon library by category tabs,
 *     with a search across every category ("Search all icons...");
 *   - click an icon to select it, then Select; a double-click picks at once.
 * Icons load as they scroll into view, 60 at a time (a "Show more icons"
 * button does the same), since the library holds several hundred.
 * Inside a locked field scope (components/ui/FieldLock) the field only shows
 * the chosen icon: it opens no dialog and is not greyed out.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ImageOff, Search } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { useFieldLocked } from '@/components/ui/FieldLock';
import { cn } from '@/lib/utils';
import { listTagIconCatalog, useTagIconByPath, type TagIconCategory } from '@/services/patientTags.service';

const CHUNK = 60;

/** One library icon, or corp's ImageOff placeholder when it cannot be shown. */
export function TagIconImage({ path, size = 20, className }: { path: string | null | undefined; size?: number; className?: string }) {
  const { url, loading } = useTagIconByPath(path);
  const style = { width: size, height: size };
  if (url) return <img src={url} alt="" style={style} className={cn('shrink-0 object-contain', className)} />;
  return (
    <span style={style} className={cn('inline-flex shrink-0 items-center justify-center text-slate-300', className)} aria-hidden>
      {loading ? null : <ImageOff style={{ width: size * 0.8, height: size * 0.8 }} />}
    </span>
  );
}

export interface TagIconPickerProps {
  id?: string;
  value: string;
  onChange: (path: string) => void;
  /** Amber, as corp's "warning" field state, while no icon is chosen. */
  warn?: boolean;
}

export function TagIconPicker({ id, value, onChange, warn }: TagIconPickerProps) {
  const [open, setOpen] = useState(false);
  const locked = useFieldLocked();
  const className = cn('form-input-base flex w-full items-center gap-3 text-left', warn && 'border-amber-400 ring-1 ring-amber-200');
  const shown = value ? (
    <>
      <TagIconImage path={value} size={24} />
      <span className="truncate text-sm text-slate-700">{value}</span>
    </>
  ) : (
    <span className="text-sm text-slate-400">{locked ? '—' : 'Choose an icon'}</span>
  );
  // Locked: the chosen icon, as plain content.
  if (locked) {
    return (
      <div id={id} className={className}>
        {shown}
      </div>
    );
  }
  return (
    <>
      <button id={id} type="button" onClick={() => setOpen(true)} className={className}>
        {shown}
      </button>
      {open && (
        <TagIconDialog
          current={value}
          onClose={() => setOpen(false)}
          onPick={(path) => {
            onChange(path);
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

function TagIconDialog({ current, onClose, onPick }: { current: string; onClose: () => void; onPick: (path: string) => void }) {
  const [catalog, setCatalog] = useState<TagIconCategory[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(current);
  const [shown, setShown] = useState(CHUNK);
  const sentinel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    listTagIconCatalog()
      .then((c) => {
        if (cancelled) return;
        setCatalog(c);
        // Open on the chosen icon's category, else the first.
        const own = c.find((cat) => cat.icons.some((i) => i.path === current));
        setCategory((own ?? c[0])?.category ?? '');
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [current]);

  const query = search.trim().toLowerCase();
  const icons = useMemo(() => {
    if (!catalog) return [];
    if (query) return catalog.flatMap((c) => c.icons).filter((i) => i.path.toLowerCase().includes(query));
    return catalog.find((c) => c.category === category)?.icons ?? [];
  }, [catalog, category, query]);

  useEffect(() => setShown(CHUNK), [category, query]);

  // Load the next chunk as the end of the grid scrolls into view.
  useEffect(() => {
    const el = sentinel.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setShown((n) => n + CHUNK);
    });
    io.observe(el);
    return () => io.disconnect();
  }, [icons, shown]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="xl"
      title={<h2 className="text-lg font-semibold leading-none text-slate-900">Choose Icon</h2>}
      footer={
        <div className="flex w-full items-center gap-3">
          <span className="min-w-0 flex-1 truncate text-xs text-slate-500">{selected || 'No icon selected'}</span>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!selected} onClick={() => onPick(selected)}>
            Select
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Input
          aria-label="Search all icons"
          placeholder="Search all icons..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leftAddon={<Search className="h-4 w-4 text-slate-400" />}
        />
        {failed ? (
          <p className="py-8 text-center text-sm text-slate-500">The icon library could not be loaded.</p>
        ) : !catalog ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <>
            {!query && (
              <div role="tablist" aria-label="Icon categories" className="flex flex-wrap gap-1">
                {catalog.map((c) => (
                  <button
                    key={c.category}
                    type="button"
                    role="tab"
                    aria-selected={c.category === category}
                    onClick={() => setCategory(c.category)}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                      c.category === category
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                    )}
                  >
                    {c.category}
                  </button>
                ))}
              </div>
            )}
            {icons.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-500">No icons found.</p>
            ) : (
              <div className="max-h-[50vh] overflow-y-auto">
                <div className="grid grid-cols-1 gap-2 min-[360px]:grid-cols-6 sm:grid-cols-8 md:grid-cols-10">
                  {icons.slice(0, shown).map((i) => (
                    <button
                      key={i.path}
                      type="button"
                      title={i.path}
                      aria-label={i.path}
                      aria-pressed={selected === i.path}
                      onClick={() => setSelected(i.path)}
                      onDoubleClick={() => onPick(i.path)}
                      className={cn(
                        'flex aspect-square items-center justify-center rounded-md border p-1.5 transition-colors',
                        selected === i.path ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-slate-200 hover:bg-slate-50',
                      )}
                    >
                      <TagIconImage path={i.path} size={28} />
                    </button>
                  ))}
                </div>
                {shown < icons.length && (
                  <div ref={sentinel} className="flex justify-center py-3">
                    <Button variant="ghost" size="sm" onClick={() => setShown((n) => n + CHUNK)}>
                      Show more icons
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
